import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import {
  createPromotionApi,
  deletePromotionApi,
  getProfileBundle,
  saveProfile,
  updatePromotionApi,
} from './api';
import { ProfileEditor } from './ProfileEditor';
import { PromotionsEditor } from './PromotionsEditor';

// THE STAFF HOST OF THE BUSINESS PROFILE + PROMOTIONS EDITORS, for one account (Run N; moved out of the old
// /business-profile/:accountId page in the KyckConnect redesign, Phase 1). The account's KyckConnect -> Setup -> Business profile
// step hosts it, and the old URL redirects there. Every save is audited server-side with surface 'kyck'.

export function BusinessProfileEditors({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const key = ['business-profile', accountId];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => getProfileBundle(accountId) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  if (isLoading) return <Spinner />;
  if (error || !data) {
    return <p className="text-sm text-rose-600">{error instanceof Error ? error.message : 'Could not load this profile.'}</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">
        What the assistant knows and may say. Promotions below are checked against today&apos;s date in the club&apos;s own timezone.
      </p>
      <PromotionsEditor
        promotions={data.promotions}
        create={async (input) => {
          await createPromotionApi(accountId, input);
          await refresh();
        }}
        update={async (id, input) => {
          await updatePromotionApi(accountId, id, input);
          await refresh();
        }}
        remove={async (id) => {
          await deletePromotionApi(accountId, id);
          await refresh();
        }}
      />
      <ProfileEditor
        accountName={data.accountName}
        profile={data.profile}
        timezone={data.timezone}
        save={async (input) => {
          await saveProfile(accountId, input);
          await refresh();
        }}
      />
    </div>
  );
}
